import { Editor } from 'primereact/editor';
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import DOMPurify, { type Config } from 'dompurify';

type PrimeReactEditorProps = {
  onBlur?: any;
  name?: string;
  onChange?: any;
  value?: string;
  maxlength?: number;
};

export type PrimeReactEditorRef = {
  focus: () => void;
  blur: () => void;
  getQuill: () => any;
};

const PrimeReactEditor = forwardRef<PrimeReactEditorRef, PrimeReactEditorProps>(
  ({ onBlur, name, onChange, value, maxlength }, forwardedRef) => {
    const editorRef = useRef<any>(null);

    useImperativeHandle(forwardedRef, () => ({
      focus: () => {
        if (editorRef.current) {
          const quill = editorRef.current.getQuill();
          if (quill && quill.focus) {
            quill.focus();
          } else if (editorRef.current.focus) {
            editorRef.current.focus();
          }
        }
      },
      blur: () => {
        if (editorRef.current) {
          const quill = editorRef.current.getQuill();
          if (quill && quill.blur) {
            quill.blur();
          } else if (editorRef.current.blur) {
            editorRef.current.blur();
          }
        }
      },
      getQuill: () => {
        return editorRef.current?.getQuill?.();
      },
    }));
    const handleRef = useCallback(
      (el: any) => {
        editorRef.current = el;

        if (forwardedRef) {
          if (typeof forwardedRef === 'function') {
            forwardedRef(el);
          } else {
            forwardedRef.current = el;
          }
        }
      },
      [forwardedRef]
    );

    const ToolbarHeader = useMemo(() => {
      const handleUndo = () => {
        if (editorRef.current) {
          const quill = editorRef.current.getQuill();
          quill.history.undo();
        }
      };

      const handleRedo = () => {
        if (editorRef.current) {
          const quill = editorRef.current.getQuill();
          quill.history.redo();
        }
      };
      return (
        <div>
          <span className="ql-formats">
            <button
              type="button"
              className="ql-bold"
              aria-label="Bold"
              title="Bold"
            />
            <button
              type="button"
              className="ql-italic"
              aria-label="Italic"
              title="Italic"
            />
            <button
              type="button"
              className="ql-underline"
              aria-label="Underline"
              title="Underline"
            />
            <button
              type="button"
              className="ql-strike"
              aria-label="Strike"
              title="Strike"
            />
          </span>
          <span className="ql-formats">
            <button
              className="ql-list"
              value="ordered"
              aria-label="Ordered List"
              type="button"
              title="Ordered List"
            />
            <button
              className="ql-list"
              value="bullet"
              aria-label="Bullet List"
              type="button"
              title="Bullet List"
            />
            <button
              type="button"
              className="ql-indent"
              value="-1"
              aria-label="Decrease Indent"
              title="Decrease Indent"
            />
            <button
              type="button"
              className="ql-indent"
              value="+1"
              aria-label="Increase Indent"
              title="Increase Indent"
            />
          </span>
          <span className="ql-formats">
            <button
              type="button"
              onClick={handleUndo}
              aria-label="Undo"
              title="Undo"
              className=""
            >
              <i className="pi pi-undo flex" />
            </button>
            <button
              type="button"
              onClick={handleRedo}
              aria-label="Redo"
              title="Redo"
              className=""
            >
              <i className="pi pi-undo scale-x-[-1] flex" />
            </button>
          </span>
          <span className="ql-formats">
            <button
              className="ql-clean"
              aria-label="Clean Format"
              title="Clean Format"
              type="button"
            />
          </span>
        </div>
      );
    }, []);

    const quillFormats = [
      'bold',
      'italic',
      'underline',
      'strike',
      'list',
      'bullet',
      'indent',
    ];

    const sanatizeConfig: Config = {
      ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'em',
        'u',
        's',
        'ul',
        'ol',
        'li',
        'span',
      ],

      ALLOWED_ATTR: ['style'],

      ALLOW_DATA_ATTR: false,
      ALLOW_ARIA_ATTR: false,
      USE_PROFILES: {
        html: true,
        svg: false,
        svgFilters: false,
        mathMl: false,
      },

      SANITIZE_NAMED_PROPS: true,

      CUSTOM_ELEMENT_HANDLING: {
        tagNameCheck: null,
        attributeNameCheck: null,
        allowCustomizedBuiltInElements: false,
      },
    };

    return (
      <Editor
        headerTemplate={ToolbarHeader}
        style={{ height: '150px' }}
        ref={handleRef}
        value={value}
        onTextChange={(e) => {
          if (onChange && e.htmlValue !== undefined) {
            onChange(DOMPurify.sanitize(e.htmlValue as string, sanatizeConfig));
          }
        }}
        onBlur={onBlur}
        name={name}
        maxLength={maxlength}
        formats={quillFormats}
      />
    );
  }
);

PrimeReactEditor.defaultProps = {
  onBlur: undefined,
  name: '',
  onChange: undefined,
  value: '',
  maxlength: undefined,
};

export default PrimeReactEditor;
